// Per-route error boundary. Wraps each route's Outlet child so a crash in one
// screen does not unmount the AppShell (nav, header stay intact).
//
// Usage:
//   <RouteErrorBoundary>
//     <SomePage />
//   </RouteErrorBoundary>

import { ErrorBoundary } from "@/components/ErrorBoundary";
import type { ReactNode } from "react";

function RouteErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col items-start gap-4 rounded-lg border border-red-900/50 bg-red-950/20 p-8"
    >
      <div>
        <h2 className="text-lg font-semibold text-red-300">Something went wrong on this page.</h2>
        <p className="mt-1 text-sm text-zinc-400">
          The rest of the app is unaffected. You can try refreshing this section.
        </p>
      </div>
      <pre className="max-h-40 w-full overflow-auto rounded bg-[#1a1a1a] p-3 text-xs text-red-400 whitespace-pre-wrap">
        {error.message}
      </pre>
      <button
        type="button"
        onClick={reset}
        className="rounded bg-[#CCFF00] px-4 py-2 text-sm font-semibold text-black hover:bg-[#b8e600]"
      >
        Try again
      </button>
    </div>
  );
}

export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={(error, reset) => <RouteErrorFallback error={error} reset={reset} />}
    >
      {children}
    </ErrorBoundary>
  );
}
